import { Caption, Label } from "@pej/kore/components";
import type { ReactNode } from "react";

export interface RangeControlProps {
    /** Visible label, also the accessible name. */
    label: string;
    value: number;
    onChange: (value: number) => void;
    /** Unique on the page: several of these share one form. */
    id: string;
    min: number;
    max: number;
    step: number;
    /** Double-clicking the read-out returns here. */
    defaultValue: number;
    description?: ReactNode;
}

/**
 * A native range with a percent read-out, ported from vouchers' design-template
 * editor so the two products' sliders look and behave alike — no headless
 * slider dependency. The read-out is a button so the reset is reachable.
 */
export function RangeControl({
    label,
    value,
    onChange,
    id,
    min,
    max,
    step,
    defaultValue,
    description,
}: RangeControlProps) {
    return (
        <div className="flex min-w-0 flex-col gap-1.5">
            <Label htmlFor={id}>{label}</Label>
            <div className="flex items-center gap-3">
                <input
                    id={id}
                    type="range"
                    className="min-w-0 flex-1 accent-primary"
                    min={min}
                    max={max}
                    step={step}
                    value={value}
                    onChange={(e) => onChange(Math.min(max, Math.max(min, e.target.valueAsNumber)))}
                    aria-label={label}
                />
                <button
                    type="button"
                    title="Double-click to reset"
                    onDoubleClick={() => onChange(defaultValue)}
                    className="w-12 shrink-0 text-right text-sm text-muted-foreground"
                >
                    {value}%
                </button>
            </div>
            {description != null && <Caption as="p">{description}</Caption>}
        </div>
    );
}
